import React, { useState, useEffect } from 'react';
import { X, Building2, Calendar, FileText, Users, ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeOptions = [
  { value: 'periodique', label: 'Périodique', description: 'Visite régulière planifiée' },
  { value: 'ponctuelle', label: 'Ponctuelle', description: 'Visite unique' },
  { value: 'urgente', label: 'Urgente', description: 'Suite à un incident' },
  { value: 'conformite', label: 'Conformité', description: 'Vérification réglementaire' }
];

export default function CreateVisiteModal({ buildings, onClose, onCreate, token }) {
  const [formData, setFormData] = useState({
    titre: '',
    building_id: '',
    type_visite: 'periodique',
    date_planifiee: '',
    description: '',
    template_id: ''
  });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    // Fetch checklist templates
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/checklist-templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTemplates(data);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    fetchTemplates();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Format date properly
    const submitData = {
      ...formData,
      date_planifiee: new Date(formData.date_planifiee).toISOString()
    };
    
    await onCreate(submitData);
    setLoading(false);
  };

  const isStep1Valid = formData.titre && formData.building_id && formData.date_planifiee;
  const isStep2Valid = formData.type_visite;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Only close if clicking the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" 
        data-testid="create-visite-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Nouvelle visite d'immeuble</h2>
            <p className="text-sm text-gray-500 mt-1">Étape {step} sur 2</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex gap-2">
            <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-[#00D3A8]' : 'bg-gray-200'}`}></div>
            <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-[#00D3A8]' : 'bg-gray-200'}`}></div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {step === 1 && (
              <div className="space-y-6">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#00D3A8]" />
                  Informations générales
                </h3>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre de la visite *
                  </label>
                  <input
                    type="text"
                    value={formData.titre}
                    onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                    placeholder="Ex: Visite trimestrielle Q1 2026"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    required
                    data-testid="visite-titre-input"
                  />
                </div>

                {/* Building */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="h-4 w-4 inline mr-1" />
                    Immeuble *
                  </label>
                  <select
                    value={formData.building_id}
                    onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] bg-white"
                    required
                    data-testid="visite-building-select"
                  >
                    <option value="">Sélectionner un immeuble</option>
                    {buildings.map(building => (
                      <option key={building.id} value={building.id}>
                        {building.nom} - {building.adresse}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    Date et heure prévues *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.date_planifiee}
                    onChange={(e) => setFormData({ ...formData, date_planifiee: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                    required
                    data-testid="visite-date-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optionnel)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Objectifs de la visite, points particuliers à vérifier..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] resize-none"
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-[#00D3A8]" />
                  Type et checklist
                </h3>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type de visite *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {typeOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => setFormData({ ...formData, type_visite: option.value })}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          formData.type_visite === option.value
                            ? 'border-[#00D3A8] bg-[#00D3A8]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`type-option-${option.value}`}
                      >
                        <p className="font-medium text-gray-900">{option.label}</p>
                        <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Checklist à utiliser (optionnel)
                  </label>
                  <select
                    value={formData.template_id}
                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] bg-white"
                  >
                    <option value="">Aucune checklist</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.nom} ({template.items?.length || 0} points)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Une checklist permet de standardiser vos visites avec des points de contrôle prédéfinis.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
            <div>
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Retour
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              {step < 2 ? (
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!isStep1Valid}
                  className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || !isStep2Valid}
                  className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="submit-visite-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Créer la visite'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
